import DefaultAirlineIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/airline';
import MultipleAirlinesIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/airline--multiple';
import BpkImage, {
  BORDER_RADIUS_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BpkFlex,
  BpkHStack,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import reactReplacePlaceholder from 'react-replace-placeholder';

import type { LegWithFormattedDate } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';
import type { StringTranslations } from '@skyscanner-internal/landing-pages-shared-types/types-shared/components';

import STYLES from './Flight.module.scss';

type FlightProps = {
  strings: StringTranslations;
  leg: LegWithFormattedDate;
  direct: boolean;
};

const Flight = ({ direct, leg, strings }: FlightProps) => {
  const originCode = leg.origin && leg.origin.skyCode;
  const destinationCode = leg.destination && leg.destination.skyCode;
  const {
    carrier: { icon, name: carrierName },
    formattedDate,
  } = leg;

  const flightIconDataTestId = 'flight-default-icon';

  const { url: carrierLogo } = icon || {};
  const routeText = reactReplacePlaceholder(
    strings.component_deals_evolved_leg_airline_1,
    {
      origin: originCode,
      destination: destinationCode,
      airline: carrierName,
    },
    '{',
    '}',
  ).join('');

  const directnessText =
    direct === true
      ? strings.component_deals_evolved_direct
      : strings.component_deals_evolved_stop_1;

  const renderIcon = () => {
    if (direct) {
      return carrierLogo ? (
        <BpkImage
          aspectRatio={1}
          src={carrierLogo}
          className={STYLES.Flight__flightProviderImage}
          borderRadiusStyle={BORDER_RADIUS_STYLES.sm}
          altText={carrierName}
        />
      ) : (
        <BpkFlex
          direction="row"
          align="center"
          width="1.875rem"
          height="1.875rem"
          data-testid={flightIconDataTestId}
        >
          <DefaultAirlineIcon
            style={{ width: 25, height: 25 }}
            alt={carrierName}
          />
        </BpkFlex>
      );
    }
    return (
      <BpkFlex
        direction="row"
        align="center"
        width="1.875rem"
        height="1.875rem"
        data-testid={flightIconDataTestId}
      >
        <MultipleAirlinesIcon
          style={{ width: 25, height: 25 }}
          alt={carrierName}
        />
      </BpkFlex>
    );
  };
  return (
    <BpkVessel className={STYLES.Flight__flight}>
      <BpkHStack gap={BpkSpacing.None}>
        {renderIcon()}
        <BpkVessel className={STYLES.Flight__flightInfo}>
          <BpkFlex grow={1} direction="column" justify="space-between">
            <BpkText data-testid="deal-leg-date" textStyle={TEXT_STYLES.label1}>
              {formattedDate}
            </BpkText>
            <BpkText
              color={TEXT_COLORS.textSecondary}
              textStyle={TEXT_STYLES.xs}
            >
              {routeText}
            </BpkText>
          </BpkFlex>
        </BpkVessel>
        <BpkFlex
          direction="column"
          justify="space-between"
          alignSelf="baseline"
          grow={1}
          textAlign="end"
          data-testid="flight-leg"
        >
          <BpkText
            color={TEXT_COLORS.textPrimary}
            textStyle={TEXT_STYLES.label2}
          >
            {directnessText}
          </BpkText>
        </BpkFlex>
      </BpkHStack>
    </BpkVessel>
  );
};

export default Flight;
